/**
 * Fork a live hosted-network route into an editable copy (PRD-04 FR15/FR16).
 *
 * Returns a callback that turns a read-only GTFS network route into a real, editable
 * `FixedRoute` (+ its stops) — or returns the existing copy if it was already forked.
 * Shared by the Fixed Routes drawer's Network section and the in-map route popup so both
 * entry points behave identically. The live feed route is never mutated.
 */

import { useCallback } from 'react';

import { useFixedRoutes } from '../context/FixedRoutesContext';
import { getRouteShape, getRouteDetail } from '../services/network/networkApi';
import { networkRouteToFixedRoute, findExistingFork } from '../utils/fixedRoutes/networkRouteToFixedRoute';
import { trackAnalyticsEvent } from '../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../services/eventTaxonomy';
import type { FixedRoute } from '../types/fixedRoutes';

export interface ForkNetworkRouteResult {
  /** The editable copy (newly created, or the pre-existing fork). */
  route: FixedRoute;
  stopCount: number;
  /** false when an editable copy already existed (no new route created, no analytics). */
  created: boolean;
}

export function useForkNetworkRoute(): (
  feedId: string,
  version: string | null,
  gtfsRouteId: string,
) => Promise<ForkNetworkRouteResult | null> {
  const { routes, bulkAddStopsRoutes } = useFixedRoutes();

  return useCallback(
    async (feedId, version, gtfsRouteId) => {
      const existing = findExistingFork(routes, feedId, gtfsRouteId);
      if (existing) {
        return { route: existing, stopCount: existing.stops.length, created: false };
      }
      const [shapes, detail] = await Promise.all([
        getRouteShape(feedId, gtfsRouteId, { version: version ?? undefined }),
        getRouteDetail(feedId, gtfsRouteId, { version: version ?? undefined }).catch(() => null),
      ]);
      const shape = shapes[0];
      if (!shape) return null;
      const { route, stops } = networkRouteToFixedRoute(feedId, version, shape, detail, Date.now());
      bulkAddStopsRoutes(stops, [route], 'merge');
      // Telemetry must never break the fork.
      try {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.network_route_forked, {
          feed_id: feedId,
          gtfs_route_id: gtfsRouteId,
          fixed_route_id: route.id,
          stop_count: stops.length,
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        console.warn('[useForkNetworkRoute] fork analytics failed (non-fatal)', err);
      }
      return { route, stopCount: stops.length, created: true };
    },
    [routes, bulkAddStopsRoutes],
  );
}
